import { StyleSheet, Text, View } from "@react-pdf/renderer";
import Html from "react-pdf-html";

import * as cheerio from "cheerio";

import {
  FORM_CELL_PADDING_PX,
  getCellBorders,
  readTableLinesMode,
  TABLE_LINES_ATTRIBUTE,
  TableLinesMode
} from "components/pdf-generation-v3/form-print.utils";
import {
  DEFAULT_PDF_CSS_STYLE,
  FORM_PDF_CSS_STYLE
} from "components/pdf-generation-v3/pdf-generation-v3.utils";

// A cell's padding on each side, in page px.
export type PdfCellPadding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type PdfTableCell = {
  content: string;
  colspan: number;
  // percentage width, will be calculated from table columns definition
  widthPercent?: number;
  isHeader?: boolean;
  rawHtml?: string;
  fontSizePx?: number;
  italic?: boolean;
  textAlign?: "left" | "center" | "right";
  backgroundColor?: string;
  textColor?: string;
  // ENG-2277: a Form cell's own padding (Form tables only).
  paddingPx?: PdfCellPadding;
  // ENG-2277: the inline style a Form cell's content prints with (Form tables only): what the cell's
  // own style says about its text, a heading cell's bold and centring, and the row's colour or black.
  textStyle?: string;
};

// A run of cell text with inline formatting, extracted from simple markup
// (<strong>, <s>, <span style>, <br>, ...). Rendering runs as native nested
// <Text> keeps the cell on react-pdf's regular line-breaking, so its words
// wrap inside the cell instead of painting over the neighbor — the nested
// react-pdf-html fallback lays its content out independently of the cell
// width and overflows.
type PdfCellTextRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  underline?: boolean;
  color?: string;
  fontSizePx?: number;
};

export type PdfTableRow = {
  cells: PdfTableCell[];
};

export type PdfTableDescription = {
  columns: number[]; // percentage width per column, sums to 100
  rows: PdfTableRow[];
  borderColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted";
  borderWidth?: number;
  // ENG-2277: the table's data-lines setting. When set, each cell draws exactly the sides
  // getCellBorders gives it (the same rule as the standard path), and the table draws none.
  // SN-19960: also "none" for a table without data-lines whose every cell turns its lines off.
  linesMode?: TableLinesMode;
  // ENG-2277: a Form's table. Its cells print as on the standard path: through react-pdf-html with
  // the Form's stylesheet, inside their own padding. Other tables keep the renderer's own text styles.
  formMode?: boolean;
};

const DEFAULT_BORDER_COLOR = "#333333";
const DEFAULT_BORDER_STYLE = "solid" as const;
const DEFAULT_BORDER_WIDTH = 1;
const REFERENCE_CONTAINER_WIDTH_PT = 957; // Default content width in px for px->% conversion
const PX_PERCENT_SCALE = 0.4; // Scale px->%

const styles = StyleSheet.create({
  // react-pdf supports display: table, even if TS complains in some setups
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: DEFAULT_BORDER_STYLE,
    borderColor: DEFAULT_BORDER_COLOR,
    borderWidth: DEFAULT_BORDER_WIDTH,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    color: "#000000"
  },
  row: {
    flexDirection: "row",
    width: "100%",
    color: "#000000"
  },
  cellContainer: {
    borderStyle: DEFAULT_BORDER_STYLE,
    borderColor: DEFAULT_BORDER_COLOR,
    borderWidth: DEFAULT_BORDER_WIDTH,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 6.5,
    paddingRight: 6.5,
    paddingTop: 0,
    paddingBottom: 0,
    flexShrink: 0,
    flexGrow: 0,
    color: "#000000"
  },
  cellText: {
    marginTop: 5,
    marginBottom: 5,
    fontSize: 10,
    color: "#000000"
  },
  cellTextHeader: {
    marginTop: 5,
    marginBottom: 5,
    fontSize: 12,
    fontWeight: 500,
    color: "#000000"
  }
});

function clampTo100(values: number[]): number[] {
  // Normalize to sum 100 preserving ratios with two-decimal precision
  const numCols = Math.max(1, values.length);
  const sum = values.reduce((a, b) => a + b, 0);
  if (sum === 0) {
    const equal = 100 / numCols;
    const baseHundredths = Math.floor(equal * 100);
    const remainderHundredths = 10000 - baseHundredths * numCols;
    const result: number[] = new Array(numCols).fill(baseHundredths);
    for (let i = 0; i < remainderHundredths; i++) result[i % numCols] += 1;
    return result.map((v) => v / 100);
  }
  const scaled = values.map((v) => (v / sum) * 100);
  const floorHundredths = scaled.map((v) => Math.floor(v * 100));
  const sumHundredths = floorHundredths.reduce((a, b) => a + b, 0);
  let remainderHundredths = 10000 - sumHundredths;
  // Distribute remainder as evenly as possible starting from index 0
  const resultHundredths = [...floorHundredths];
  if (remainderHundredths > 0) {
    const base = Math.floor(remainderHundredths / numCols);
    const extra = remainderHundredths % numCols;
    for (let i = 0; i < numCols; i++) {
      resultHundredths[i] += base + (i < extra ? 1 : 0);
    }
  }
  return resultHundredths.map((v) => v / 100);
}

function clampTo100WithLock(values: number[], lockMask: boolean[]): number[] {
  const numCols = Math.max(1, values.length);
  const sum = values.reduce((a, b) => a + b, 0);
  if (sum === 0) return clampTo100(values);
  const scaled = values.map((v) => (v / sum) * 100);
  const floorHundredths = scaled.map((v) => Math.floor(v * 100));
  let remainderHundredths = 10000 - floorHundredths.reduce((a, b) => a + b, 0);
  const resultHundredths = [...floorHundredths];

  const adjustableIndexes = lockMask.some((l) => !l)
    ? lockMask.map((l, i) => (!l ? i : -1)).filter((i) => i >= 0)
    : Array.from({ length: numCols }, (_, i) => i);

  if (remainderHundredths > 0) {
    const base = Math.floor(remainderHundredths / adjustableIndexes.length);
    const extra = remainderHundredths % adjustableIndexes.length;
    for (let k = 0; k < adjustableIndexes.length; k++) {
      const i = adjustableIndexes[k];
      resultHundredths[i] += base + (k < extra ? 1 : 0);
    }
  }
  return resultHundredths.map((v) => v / 100);
}

const WIDTH_DECL_RE = /(?:^|;)\s*width\s*:\s*([^;]+)/i;

function parseWidthFromStyleOrAttr($el: cheerio.Cheerio): {
  value: number;
  unit: "percent" | "px" | "none";
} {
  const styleAttr = (($el.attr("style") as string) || "").toString();
  const styleWidthMatch = styleAttr.match(WIDTH_DECL_RE);
  const styleWidth = styleWidthMatch ? styleWidthMatch[1].trim() : "";
  const widthAttr = (
    ($el.attr("width") as string) ||
    styleWidth ||
    ""
  ).toString();
  if (!widthAttr) return { value: 0, unit: "none" };
  if (widthAttr.endsWith("%")) {
    const v = parseFloat(widthAttr.replace("%", ""));
    return Number.isFinite(v)
      ? { value: v, unit: "percent" }
      : { value: 0, unit: "none" };
  }
  const px = parseFloat(widthAttr.replace("px", ""));
  if (Number.isFinite(px)) return { value: px, unit: "px" };
  return { value: 0, unit: "none" };
}

function parseCellTextStyles(styleAttr: string): {
  fontSizePx?: number;
  italic?: boolean;
  textAlign?: "left" | "center" | "right";
  backgroundColor?: string;
  textColor?: string;
} {
  const out: {
    fontSizePx?: number;
    italic?: boolean;
    textAlign?: "left" | "center" | "right";
    backgroundColor?: string;
    textColor?: string;
  } = {};
  const style = styleAttr || "";
  const fontSizeMatch = style.match(/font-size\s*:\s*([^;]+)/i);
  if (fontSizeMatch) {
    const raw = fontSizeMatch[1].trim();
    const px = parseFloat(raw.replace("px", "").replace("pt", ""));
    if (Number.isFinite(px)) out.fontSizePx = px;
  }
  const fontStyleMatch = style.match(/font-style\s*:\s*([^;]+)/i);
  if (fontStyleMatch) {
    const v = fontStyleMatch[1].trim().toLowerCase();
    if (v.includes("italic")) out.italic = true;
  }
  const alignMatch = style.match(/text-align\s*:\s*([^;]+)/i);
  if (alignMatch) {
    const v = alignMatch[1].trim().toLowerCase();
    if (v === "left" || v === "center" || v === "right") out.textAlign = v;
  }
  const bgMatch = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
  if (bgMatch) {
    out.backgroundColor = bgMatch[1].trim();
  }
  const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  if (colorMatch) {
    out.textColor = colorMatch[1].trim();
  }
  return out;
}

// Border declarations that cannot draw a line by themselves.
const LINELESS_BORDER_PROPERTIES = new Set([
  "border-collapse",
  "border-spacing",
  "border-color"
]);

/**
 * SN-19960: what an element's own style says about its lines, judged as react-pdf draws them. It
 * fills every side whose width is not zero, and paints `none` and `hidden` solid, so the width
 * decides and not the style. "off": a zero width, or a none/hidden style with no width (the standard
 * path adds no default width to an element with its own style). "unset": no border declaration.
 * "on": anything that may draw a line, including a side or a shorthand, which keeps today's grid.
 */
const ownLines = (style: string): "off" | "unset" | "on" => {
  let lineStyle: string[] | undefined;
  let lineWidth: string[] | undefined;
  for (const declaration of style.split(";")) {
    const separator = declaration.indexOf(":");
    if (separator === -1) continue;
    const name = declaration.slice(0, separator).trim().toLowerCase();
    if (!name.startsWith("border") || LINELESS_BORDER_PROPERTIES.has(name)) {
      continue;
    }
    const tokens = declaration
      .slice(separator + 1)
      .trim()
      .toLowerCase()
      .split(/\s+/);
    if (name === "border-style") lineStyle = tokens;
    else if (name === "border-width") lineWidth = tokens;
    else return "on";
  }
  if (lineWidth) {
    return lineWidth.every((token) => parseFloat(token) === 0) ? "off" : "on";
  }
  if (lineStyle) {
    return lineStyle.every((token) => token === "none" || token === "hidden")
      ? "off"
      : "on";
  }
  return "unset";
};

const PX_LENGTH = /^\s*(\d*\.?\d+)px\s*$/i;

/**
 * A Form cell's padding from its inline style. The Form normalizer writes one on every cell (the
 * cell's own, else FORM_CELL_PADDING_PX), in px; a side it cannot read keeps the Form default.
 */
export function parseCellPaddingPx(styleAttr: string): PdfCellPadding {
  const padding: PdfCellPadding = {
    top: FORM_CELL_PADDING_PX.vertical,
    right: FORM_CELL_PADDING_PX.horizontal,
    bottom: FORM_CELL_PADDING_PX.vertical,
    left: FORM_CELL_PADDING_PX.horizontal
  };
  const px = (value: string) => {
    const match = PX_LENGTH.exec(value);
    return match ? parseFloat(match[1]) : undefined;
  };
  // In order, so a longhand after the shorthand wins, as in CSS.
  for (const declaration of (styleAttr || "").split(";")) {
    const [rawName, ...rest] = declaration.split(":");
    const name = rawName?.trim().toLowerCase();
    const value = rest.join(":").trim();
    if (name === "padding") {
      const parts = value.split(/\s+/).map(px);
      if (parts.length <= 4 && parts.every((part) => part !== undefined)) {
        const [top, right = top, bottom = top, left = right] =
          parts as number[];
        Object.assign(padding, { top, right, bottom, left });
      }
    } else if (name?.startsWith("padding-")) {
      const side = name.slice("padding-".length);
      const length = px(value);
      if (side in padding && length !== undefined) {
        padding[side as keyof PdfCellPadding] = length;
      }
    }
  }
  return padding;
}

// ENG-2277: a Form's cell content, for react-pdf-html with the Form's stylesheet — the same engine and
// rules the standard path prints it with, so its font, size, line height and inline formatting are the
// editor's whichever renderer is on.
const formCellHtml = (cell: PdfTableCell): string =>
  `<html><body><style>${FORM_PDF_CSS_STYLE}</style><div style="${(cell.textStyle ?? "").replace(/"/g, "&quot;")}">${cell.rawHtml ?? ""}</div></body></html>`;

// The cell's box is drawn by the cell itself (PdfDocumentTable's cell View): its padding, lines,
// size and background. Everything else in a cell's style is about its text.
const isCellBoxProperty = (name: string) =>
  name === "width" ||
  name === "height" ||
  name.startsWith("padding") ||
  name.startsWith("border") ||
  name.startsWith("background");

/**
 * ENG-2277: the style a Form cell's content prints with. The content goes to react-pdf-html inside
 * a wrapper of its own, so whatever the cell's style says about its text (font, weight, line
 * height, alignment...) is carried onto that wrapper; rebuilding it from a few parsed values lost
 * the rest. A heading cell is bold and centred unless it says otherwise, as a <th> is on the
 * standard path, and a cell without a colour takes its row's, or black.
 */
const formCellTextStyle = (
  cellStyle: string,
  isHeader: boolean,
  fallbackColor: string
): string => {
  const declarations = cellStyle
    .split(";")
    .map((declaration) => {
      const separator = declaration.indexOf(":");
      return separator === -1
        ? null
        : {
            name: declaration.slice(0, separator).trim().toLowerCase(),
            value: declaration.slice(separator + 1).trim()
          };
    })
    .filter(
      (declaration): declaration is { name: string; value: string } =>
        Boolean(declaration?.name && declaration.value) &&
        !isCellBoxProperty(declaration?.name ?? "")
    );
  const sets = (property: string) =>
    declarations.some(({ name }) => name === property);
  return [
    isHeader && !sets("font-weight") ? "font-weight: bold" : "",
    isHeader && !sets("text-align") ? "text-align: center" : "",
    ...declarations.map(({ name, value }) => `${name}: ${value}`),
    sets("color") ? "" : `color: ${fallbackColor}`
  ]
    .filter(Boolean)
    .join("; ");
};

// Scoped hyphenation for table cell text (passed per <Text> node — the
// app-global callback stays "never break", see pdf-document.component.tsx).
// Every token gets break opportunities every few characters so a token wider
// than its narrow cell wraps instead of painting across the neighboring
// cells — react-pdf inserts a hyphen at such breaks. Money and percent
// tokens are the exception and are kept whole even with trailing prose
// punctuation ("...owes $1,500.00."): a "$1,5-\n00.00" reads as the wrong
// number, and the invoice table generators size their money columns so those
// values always fit.
const MONEY_OR_PERCENT_TOKEN =
  /^\(?(?:[$€£][\d,]+(?:\.\d+)?|[\d,]+\.\d\d|[\d.,]+%)\)?[.,;:]?$/;
const MONEY_TOKEN_MAX_LENGTH = 16;
const UNBREAKABLE_TOKEN_MAX_LENGTH = 4;
const TOKEN_CHUNK_LENGTH = 4;

export const tableCellHyphenationCallback = (word: string): string[] => {
  const maxWholeLength = MONEY_OR_PERCENT_TOKEN.test(word)
    ? MONEY_TOKEN_MAX_LENGTH
    : UNBREAKABLE_TOKEN_MAX_LENGTH;

  if (word.length <= maxWholeLength) return [word];

  const parts: string[] = [];
  for (let i = 0; i < word.length; i += TOKEN_CHUNK_LENGTH) {
    parts.push(word.slice(i, i + TOKEN_CHUNK_LENGTH));
  }
  return parts;
};

const INLINE_RUN_TAGS = new Set([
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "span",
  "br",
  "sub",
  "sup"
]);

/**
 * Converts a cell's inner HTML into styled text runs when it only contains
 * simple inline formatting. Returns null for anything richer (images, lists,
 * nested tables, ...), which falls back to the nested react-pdf-html
 * renderer.
 */
export function extractInlineRuns(html: string): PdfCellTextRun[] | null {
  const loadFn = cheerio.load as any;
  const $ = loadFn(`<div id="__cell__">${html}</div>`);

  const runs: PdfCellTextRun[] = [];
  let supported = true;

  const walk = (
    node: any,
    style: Omit<PdfCellTextRun, "text">
  ): void => {
    if (!supported || !node) return;

    if (node.type === "text") {
      // Collapse formatting whitespace (pretty-printed HTML newlines/indents)
      // to single spaces \u2014 react-pdf <Text> would render a raw "\n" as a hard
      // line break. Explicit breaks come only from <br> runs below.
      const text = String(node.data ?? "")
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ");
      if (text.length) runs.push({ ...style, text });
      return;
    }

    if (node.type === "comment") return;

    if (node.type !== "tag" && node.type !== "style" && node.type !== "script") {
      supported = false;
      return;
    }

    const tag = String(node.tagName || node.name || "").toLowerCase();
    if (!INLINE_RUN_TAGS.has(tag)) {
      supported = false;
      return;
    }

    if (tag === "br") {
      runs.push({ ...style, text: "\n" });
      return;
    }

    const nextStyle: Omit<PdfCellTextRun, "text"> = { ...style };
    if (tag === "strong" || tag === "b") nextStyle.bold = true;
    if (tag === "em" || tag === "i") nextStyle.italic = true;
    if (tag === "s" || tag === "strike" || tag === "del") {
      nextStyle.strike = true;
    }
    if (tag === "u") nextStyle.underline = true;

    const styleAttr = ($(node).attr("style") as string) || "";
    if (styleAttr) {
      const parsed = parseCellTextStyles(styleAttr);
      if (parsed.fontSizePx !== undefined) {
        nextStyle.fontSizePx = parsed.fontSizePx;
      }
      if (parsed.textColor) nextStyle.color = parsed.textColor;
      if (parsed.italic) nextStyle.italic = true;
      if (/text-decoration[^;]*line-through/i.test(styleAttr)) {
        nextStyle.strike = true;
      }
      const weightMatch = styleAttr.match(/font-weight\s*:\s*([^;]+)/i);
      if (weightMatch) {
        const weight = weightMatch[1].trim().toLowerCase();
        if (weight === "bold" || Number(weight) >= 600) nextStyle.bold = true;
      }
    }

    (node.childNodes || node.children || []).forEach((child: any) =>
      walk(child, nextStyle)
    );
  };

  const root = $("#__cell__").get(0);
  if (!root) return null;
  (root.childNodes || root.children || []).forEach((child: any) =>
    walk(child, {})
  );

  if (!supported) return null;
  return runs;
}

function parseRowStyles(styleAttr: string): {
  backgroundColor?: string;
  textColor?: string;
} {
  const style = styleAttr || "";
  const out: { backgroundColor?: string; textColor?: string } = {};
  const bgMatch = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
  if (bgMatch) out.backgroundColor = bgMatch[1].trim();
  const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  if (colorMatch) out.textColor = colorMatch[1].trim();
  return out;
}

/**
 * The rows a table is described from. ENG-2277: a Form's table reads its own rows only (`ownOnly`),
 * as the standard path does. A table nested in one of its cells prints inside that cell, from the
 * cell's HTML, so counting its rows here as well would print them twice and size the outer
 * table's columns from the nested one. Other templates keep every row under the table, as before.
 */
function tableRows(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  ownOnly: boolean
): cheerio.Cheerio {
  const rows = $table.find("tr");
  if (!ownOnly) return rows;
  const table = $table.get(0);
  return rows.filter(
    (_: number, row: cheerio.Element) =>
      $(row).closest("table").get(0) === table
  );
}

function deriveColumnPercentsFromColgroup(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  maxCols: number,
  ownOnly: boolean
): number[] | null {
  const $colgroup = ownOnly
    ? $table.children("colgroup").first()
    : $table.find("colgroup").first();
  if ($colgroup.length === 0) return null;

  const colWidths: number[] = [];
  $colgroup.find("col").each((_: number, col: cheerio.Element) => {
    const spec = parseWidthFromStyleOrAttr($(col));
    if (spec.unit === "percent" && spec.value > 0) {
      colWidths.push(spec.value);
    } else {
      colWidths.push(0); // Will be filled later
    }
  });

  if (colWidths.length !== maxCols) return null;

  // Fill any zero-width columns with equal distribution of remaining space
  const totalSpecified = colWidths.reduce((a, b) => a + b, 0);
  const unspecifiedCount = colWidths.filter((w) => w === 0).length;

  if (totalSpecified < 100 && unspecifiedCount > 0) {
    const remainingPercent = 100 - totalSpecified;
    const equalShare = remainingPercent / unspecifiedCount;
    for (let i = 0; i < colWidths.length; i++) {
      if (colWidths[i] === 0) {
        colWidths[i] = equalShare;
      }
    }
  }

  return clampTo100(colWidths);
}

function deriveColumnPercentsFromTd(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  maxCols: number,
  referenceWidthPx: number,
  ownRowsOnly: boolean
): number[] | null {
  // Find the first row that provides any width info
  let selectedRow: cheerio.Element | null = null;
  tableRows($, $table, ownRowsOnly).each((_: number, tr: cheerio.Element) => {
    if (selectedRow) return;
    const hasWidth = $(tr)
      .children("th,td")
      .toArray()
      .some((cell) => {
        const spec = parseWidthFromStyleOrAttr($(cell as any));
        return spec.unit !== "none" && spec.value > 0;
      });
    if (hasWidth) selectedRow = tr;
  });

  if (!selectedRow) return null;

  type CellSpec = {
    colspan: number;
    unit: "percent" | "px" | "none";
    value: number;
  };
  const cellSpecs: CellSpec[] = [];
  $(selectedRow)
    .children("th,td")
    .each((__: number, cell: cheerio.Element) => {
      const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
      const spec = parseWidthFromStyleOrAttr($(cell));
      cellSpecs.push({ colspan, unit: spec.unit, value: spec.value });
    });

  // Use the provided reference width directly to ensure identical px-based columns across tables
  const effectiveRef = referenceWidthPx;

  // Convert cell specs to percent values (pre-distribution), separating px and percent
  type PercentSpec = { colspan: number; percent: number; isPx: boolean };
  const percentSpecs: PercentSpec[] = cellSpecs.map((s) => {
    if (s.unit === "percent") {
      return { colspan: s.colspan, percent: Math.max(0, s.value), isPx: false };
    }
    if (s.unit === "px") {
      const pct =
        Math.max(0, (s.value / effectiveRef) * 100) * PX_PERCENT_SCALE;
      return { colspan: s.colspan, percent: pct, isPx: true };
    }
    return { colspan: s.colspan, percent: 0, isPx: false };
  });

  // If both px and percent are present and the sum exceeds 100,
  // scale down percent-based totals so px-based totals remain intact
  const pxTotal = percentSpecs
    .filter((p) => p.isPx)
    .reduce((a, b) => a + b.percent, 0);
  const pctTotal = percentSpecs
    .filter((p) => !p.isPx)
    .reduce((a, b) => a + b.percent, 0);
  if (pxTotal > 0 && pctTotal > 0 && pxTotal + pctTotal > 100) {
    const allowedForPct = Math.max(0, 100 - pxTotal);
    const scale = pctTotal > 0 ? allowedForPct / pctTotal : 1;
    for (const p of percentSpecs) {
      if (!p.isPx) p.percent = p.percent * scale;
    }
  }

  const colValues = new Array(Math.max(1, maxCols)).fill(0);
  const specifiedMask = new Array(Math.max(1, maxCols)).fill(false);
  const pxSpecifiedMask = new Array(Math.max(1, maxCols)).fill(false);

  let colCursor = 0;
  for (const spec of percentSpecs) {
    const { colspan, percent, isPx } = spec;
    const perCol = percent / Math.max(1, colspan);
    for (let i = 0; i < colspan; i++) {
      const idx = colCursor + i;
      if (idx < colValues.length) {
        colValues[idx] += perCol;
        if (percent > 0) specifiedMask[idx] = true;
        if (isPx && percent > 0) pxSpecifiedMask[idx] = true;
      }
    }
    colCursor += Math.max(1, colspan);
  }

  const totalSpecified = colValues.reduce((a, b) => a + b, 0);
  const unspecifiedIndexes: number[] = [];
  for (let i = 0; i < colValues.length; i++) {
    if (!specifiedMask[i]) unspecifiedIndexes.push(i);
  }

  if (totalSpecified <= 0) return null;

  if (totalSpecified < 100 && unspecifiedIndexes.length > 0) {
    const remainder = 100 - totalSpecified;
    const add = remainder / unspecifiedIndexes.length;
    for (const idx of unspecifiedIndexes) colValues[idx] = add;
  }

  // Preserve px-based columns during rounding so identical px widths look identical across tables
  return clampTo100WithLock(colValues, pxSpecifiedMask);
}

function detectMaxColumns(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  ownRowsOnly: boolean
): number {
  let maxCols = 0;
  const rows = tableRows($, $table, ownRowsOnly);
  rows.each((_: number, tr: cheerio.Element) => {
    let cols = 0;
    $(tr)
      .children("th,td")
      .each((__: number, cell: cheerio.Element) => {
        const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
        cols += Math.max(1, colspan);
      });
    if (cols > maxCols) maxCols = cols;
  });

  // Also check if any row has cells that explicitly define a higher column count
  // by looking at the first row (header row) which should define the base structure
  const firstRow = rows.first();
  if (firstRow.length > 0) {
    const headerCells = firstRow.children("th,td").length;
    if (headerCells > maxCols) maxCols = headerCells;
  }

  return maxCols || 1;
}

function equalSplit(count: number): number[] {
  return clampTo100(new Array(Math.max(1, count)).fill(1));
}

export function parseHtmlTableToDescription(
  html: string,
  options?: { containerWidthPx?: number; formMode?: boolean }
): PdfTableDescription | null {
  const loadFn = cheerio.load as any;
  const $ = loadFn(html);
  const $table = $("table").first();
  if ($table.length === 0) return null;

  const formMode = Boolean(options?.formMode);

  // Establish column widths
  const maxCols = detectMaxColumns($, $table, formMode);
  const referenceWidthPx =
    options?.containerWidthPx ?? REFERENCE_CONTAINER_WIDTH_PT;

  // Try colgroup first, then fall back to TD-based detection
  let colPercents = deriveColumnPercentsFromColgroup(
    $,
    $table,
    maxCols,
    formMode
  );
  if (!colPercents) {
    colPercents = deriveColumnPercentsFromTd(
      $,
      $table,
      maxCols,
      referenceWidthPx,
      formMode
    );
  }
  if (!colPercents) {
    colPercents = equalSplit(maxCols);
  }

  const rows: PdfTableRow[] = [];
  let cellCount = 0;
  let everyCellOff = true;
  tableRows($, $table, formMode).each((_: number, tr: cheerio.Element) => {
    const rowCells: PdfTableCell[] = [];
    const rowStyleInherit = parseRowStyles(
      ($(tr).attr("style") as string) || ""
    );
    $(tr)
      .children("th,td")
      .each((__: number, cell: cheerio.Element) => {
        const tag = ($(cell).get(0)?.tagName || "").toLowerCase();
        const isHeader = tag === "th";
        const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
        // Extract content
        const innerHtml = $(cell).html() || "";
        let content = $(cell).text();
        content = content.replace(/\u00A0/g, " ").trim();
        // Parse per-cell text styles
        const cellStyle = ($(cell).attr("style") as string) || "";
        cellCount += 1;
        if (ownLines(cellStyle) !== "off") everyCellOff = false;
        const styles = parseCellTextStyles(cellStyle);
        const textColor =
          styles.textColor || rowStyleInherit.textColor || "#000000";

        rowCells.push({
          content,
          rawHtml: innerHtml,
          colspan: Math.max(1, colspan),
          isHeader,
          fontSizePx: styles.fontSizePx,
          italic: styles.italic,
          textAlign: styles.textAlign,
          backgroundColor:
            styles.backgroundColor || rowStyleInherit.backgroundColor,
          textColor,
          ...(formMode
            ? {
                paddingPx: parseCellPaddingPx(cellStyle),
                textStyle: formCellTextStyle(
                  cellStyle,
                  isHeader,
                  rowStyleInherit.textColor || "#000000"
                )
              }
            : {})
        });
      });

    rows.push({ cells: rowCells });
  });

  // SN-19960: on the standard path a table whose every cell turns its lines off prints none, unless
  // the table's own style (kept when layout fixes are on) draws a box. Under ETR it then prints as
  // data-lines="none" would. An "unset" cell is not off: the standard path gives it the default grid.
  const tableDrawsNoBox =
    ownLines(($table.attr("style") as string) || "") !== "on";
  const linesMode =
    readTableLinesMode($table.attr(TABLE_LINES_ATTRIBUTE)) ??
    (cellCount > 0 && everyCellOff && tableDrawsNoBox ? "none" : null);

  return {
    columns: colPercents,
    rows,
    borderColor: DEFAULT_BORDER_COLOR,
    borderStyle: DEFAULT_BORDER_STYLE,
    borderWidth: DEFAULT_BORDER_WIDTH,
    ...(linesMode ? { linesMode } : {}),
    ...(formMode ? { formMode: true } : {})
  };
}

function computeCellWidthPercent(
  columns: number[],
  startColIndex: number,
  colspan: number
): number {
  const totalCols = columns.length;
  const span = Math.max(1, colspan);

  // Ensure we don't start beyond the table
  if (startColIndex >= totalCols) return 0;

  // Calculate the actual end index
  const end = Math.min(totalCols, startColIndex + span);
  let sum = 0;
  for (let i = startColIndex; i < end; i++) {
    sum += columns[i] || 0;
  }
  return sum;
}

function allocateWidthForCell(
  columns: number[],
  colCursor: number,
  span: number,
  rowAccumulated: number
): { width: number; nextCursor: number } {
  const totalCols = columns.length;
  const remainingCols = Math.max(0, totalCols - colCursor);
  const requested = Math.max(1, span);

  // If we're at or beyond the total columns, don't allocate any width
  if (colCursor >= totalCols) {
    return { width: 0, nextCursor: colCursor };
  }

  // Calculate actual span considering remaining columns
  const actualSpan = Math.min(requested, remainingCols);
  const width = computeCellWidthPercent(columns, colCursor, actualSpan);
  return { width, nextCursor: colCursor + actualSpan };
}

// Per-side widths for a cell in a data-lines table. Written after `borderWidth` so they win.
function cellLineWidths(
  mode: TableLinesMode,
  row: number,
  col: number,
  colSpan: number,
  rowCount: number,
  colCount: number
) {
  const sides = getCellBorders(mode, { row, col, colSpan, rowCount, colCount });
  return {
    borderTopWidth: sides.top ? DEFAULT_BORDER_WIDTH : 0,
    borderRightWidth: sides.right ? DEFAULT_BORDER_WIDTH : 0,
    borderBottomWidth: sides.bottom ? DEFAULT_BORDER_WIDTH : 0,
    borderLeftWidth: sides.left ? DEFAULT_BORDER_WIDTH : 0
  };
}

// Horizontal cell padding by table density: 6.5pt is comfortable for a
// handful of columns but wastes over half the page across 20+ of them.
function getCellHorizontalPadding(columnCount: number): number {
  if (columnCount >= 16) return 2;
  if (columnCount >= 10) return 4;
  return 6.5;
}

export default function PdfDocumentTable({
  description
}: {
  description: PdfTableDescription;
}) {
  const borderColor = description.borderColor || DEFAULT_BORDER_COLOR;
  const borderStyle = description.borderStyle || DEFAULT_BORDER_STYLE;
  const borderWidth = description.borderWidth ?? DEFAULT_BORDER_WIDTH;
  const cellHorizontalPadding = getCellHorizontalPadding(
    description.columns.length
  );
  return (
    <View
      style={{
        ...styles.table,
        borderColor,
        borderStyle,
        borderWidth: description.linesMode ? 0 : borderWidth
      }}
    >
      {description.rows.map((row, rowIndex) => {
        return (
          <View key={rowIndex} style={styles.row}>
            {(() => {
              let colCursor = 0;
              let rowAccumulated = 0;
              return row.cells.map((cell, cellIndex) => {
                const startCol = colCursor;
                const formPadding = description.formMode
                  ? cell.paddingPx
                  : undefined;
                const { width, nextCursor } = allocateWidthForCell(
                  description.columns,
                  colCursor,
                  cell.colspan,
                  rowAccumulated
                );
                rowAccumulated += width;
                colCursor = nextCursor;
                const hasMarkup = Boolean(
                  cell.rawHtml && /<[^>]+>/.test(cell.rawHtml)
                );
                const inlineRuns = hasMarkup
                  ? extractInlineRuns(cell.rawHtml as string)
                  : null;

                const baseTextStyle = {
                  ...(cell.isHeader
                    ? (styles.cellTextHeader as any)
                    : (styles.cellText as any)),
                  textAlign: cell.textAlign,
                  fontStyle: cell.italic ? "italic" : undefined,
                  fontSize: cell.fontSizePx ?? undefined,
                  color: cell.textColor ?? "#000000"
                };

                return (
                  <View
                    key={cellIndex}
                    style={{
                      ...styles.cellContainer,
                      paddingLeft: formPadding?.left ?? cellHorizontalPadding,
                      paddingRight: formPadding?.right ?? cellHorizontalPadding,
                      ...(formPadding
                        ? {
                            paddingTop: formPadding.top,
                            paddingBottom: formPadding.bottom
                          }
                        : {}),
                      borderColor,
                      borderStyle,
                      borderWidth,
                      ...(description.linesMode
                        ? cellLineWidths(
                            description.linesMode,
                            rowIndex,
                            startCol,
                            cell.colspan,
                            description.rows.length,
                            description.columns.length
                          )
                        : {}),
                      width: `${width}%`,
                      minWidth: `${width}%`,
                      maxWidth: `${width}%`,
                      backgroundColor: cell.backgroundColor
                    }}
                  >
                    {description.formMode ? (
                      <Html>{formCellHtml(cell)}</Html>
                    ) : hasMarkup && inlineRuns ? (
                      <Text
                        style={baseTextStyle}
                        hyphenationCallback={tableCellHyphenationCallback}
                      >
                        {inlineRuns.map((run, runIndex) => (
                          <Text
                            key={runIndex}
                            style={{
                              fontWeight: run.bold ? 700 : undefined,
                              fontStyle: run.italic ? "italic" : undefined,
                              textDecoration:
                                [
                                  run.strike ? "line-through" : "",
                                  run.underline ? "underline" : ""
                                ]
                                  .filter(Boolean)
                                  .join(" ") || undefined,
                              color: run.color,
                              fontSize: run.fontSizePx
                            }}
                          >
                            {run.text}
                          </Text>
                        ))}
                      </Text>
                    ) : hasMarkup ? (
                      <Html>
                        {`<html><body><style>${DEFAULT_PDF_CSS_STYLE}</style><div style="${[
                          cell.textAlign ? `text-align: ${cell.textAlign}` : "",
                          cell.italic ? "font-style: italic" : "",
                          cell.fontSizePx
                            ? `font-size: ${cell.fontSizePx}px`
                            : "",
                          `color: ${cell.textColor ?? "#000000"}`,
                          cell.backgroundColor
                            ? `background-color: ${cell.backgroundColor}`
                            : ""
                        ]
                          .filter(Boolean)
                          .join(
                            ";"
                          )}"><span style="color: ${cell.textColor ?? "#000000"}">${cell.rawHtml}</span></div></body></html>`}
                      </Html>
                    ) : (
                      <Text
                        style={baseTextStyle}
                        hyphenationCallback={tableCellHyphenationCallback}
                      >
                        {cell.content ||
                          (cell.rawHtml ?? "")
                            .replace(/<[^>]*>/g, "")
                            .replace(/&nbsp;/gi, " ")}
                      </Text>
                    )}
                  </View>
                );
              });
            })()}
          </View>
        );
      })}
    </View>
  );
}

export function buildTableDescriptionFromHtml(
  html: string,
  options?: { containerWidthPx?: number; formMode?: boolean }
): PdfTableDescription | null {
  return parseHtmlTableToDescription(html, options);
}
